import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { ActionType } from './common-action-type';
import {
    TOOLBAR_FORMAT,
    TOOLBAR_FONT_UP,
    TOOLBAR_FONT_DOWN,
    TOOLBAR_PICTURE,
    TOOLBAR_MERGECELL,
    TOOLBAR_REMOVEGROUP,
    TOOLBAR_DROPDOWN,
    TOOLBAR_ATTACHMENT_DELETE,
    TOOLBAR_PIC_RESTORE,
    TOOLBAR_IMAGE_RECOGNITION,
    TOOLBAR_PLUGIN,
    INSERT_CHART,
    TOOLBAR_CLIP,
    TOOLBAR_REPLACE_PICTURE_ICON,
    TOOLBAR_RESET_PICTURE_ICON,
    TOOLBAR_ADDGROUP,
    TOOLBAR_GENERATE_IMAGE,
    MORE_SETTING,
} from '../../img/pageIndex';
import { dataWithId, DropdownType, ToolListType } from './toolbar-data';
import i18n from '@i18next/i18n';
import { getToggleStatusByType, ToggleType } from '@src/slide/infrastructure/module/dependent/config/toggle-config';

// 是否需要屏蔽下载文件入口(比如：桌面端本地编辑场景)
const ENABLE_DOWNLOAD_MEDIA = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.downloadMedia) ?? true;
// 关闭插入公式能力时，需同时屏蔽识别公式&编辑公式&双击打开公式
const ENABLE_LATEX = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.insertLatex) ?? true;
// 插件能力
const ENABLE_ADDON = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.addonUnofficial) ?? true;
const ENABLE_OCR = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.ocr) ?? true;
const ENABLE_AI_WRITE = (
    (DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.aiGenerateText) ?? false)
    && (getToggleStatusByType(ToggleType.AI_FLOAT_BAR) ?? true)
);

export const StyleMode = {
    TOOLBAR: 'tool-bar-model',
    QUICK_SETTING: 'quick-setting-model',
} as const;

// 通用快捷设置选项（不包括文字）
export const CommonQuickItems: ToolListType[] = [
    dataWithId(ActionType.MOVE_ITEM),
];

// 更多设置
export const MoreSettingItems: ToolListType[] = [
    {
        id: ActionType.MORE_SETTING,
        tipMsg: i18n.t('更多设置'),
        icon: MORE_SETTING,
    },
];

//  多选快速工具栏
export const FloatToolbarsItems = [
    { id: ActionType.ALIGN_HORIZONTALLT_LEFT, title: i18n.t('左对齐') },
    { id: ActionType.ALIGN_HORIZONTALLT_CENTER, title: i18n.t('居中') },
    { id: ActionType.ALIGN_HORIZONTALLT_RIGHT, title: i18n.t('右对齐') },
    { id: ActionType.DIVIDER },
    { id: ActionType.ALIGN_VERTICALLY_TOP, title: i18n.t('顶部对齐') },
    { id: ActionType.ALIGN_VERTICALLY_CENTER, title: i18n.t('垂直居中') },
    { id: ActionType.ALIGN_VERTICALLY_BOTTOM, title: i18n.t('底部对齐') },
    { id: ActionType.DIVIDER },
    { id: ActionType.ALIGN_DISTRIBUTE_HORIZONTALLT, title: i18n.t('横向分散') },
    { id: ActionType.ALIGN_DISTRIBUTE_VERTICALLY, title: i18n.t('纵向分散') },
    { id: ActionType.DIVIDER },
    { id: ActionType.ADDGROUP, title: i18n.t('组合') },
];

/**
 * 选中文字的快捷选项
 * @type {*[]}
 */
export const TextQuickItems = [
    {
        id: ActionType.FORMAT_BRUSH,
        tipMsg: i18n.t('格式刷'),
        icon: TOOLBAR_FORMAT,
    },
    {
        id: ActionType.DIVIDER,
    },
    // dataWithId(ActionType.FONT),
    dataWithId(ActionType.FONTSIZE),
    {
        id: ActionType.FONTSIZE_UP,
        tipMsg: i18n.t('增大字号'),
        icon: TOOLBAR_FONT_UP,
    },
    {
        id: ActionType.FONTSIZE_DOWN,
        tipMsg: i18n.t('减小字号'),
        icon: TOOLBAR_FONT_DOWN,
    },
    {
        id: ActionType.DIVIDER,
    },
    dataWithId(ActionType.BOLD),
    dataWithId(ActionType.FORECOLOR),
].filter(Boolean);
if (ENABLE_AI_WRITE) {
    TextQuickItems.push(dataWithId(ActionType.AI_WRITE));
}

/**
 * 形状快捷设置
 * @type {*[]}
 */
export const ShapeQuickItems = [
    dataWithId(ActionType.FILLCOLOR),
    dataWithId(ActionType.BORDERCOLOR),
    dataWithId(ActionType.EFFECT),
].concat(CommonQuickItems);
if (ENABLE_AI_WRITE) {
    ShapeQuickItems.push(dataWithId(ActionType.AI_WRITE));
}

const addonItem = [
    ENABLE_ADDON && {
        id: ActionType.ADDON,
        tipMsg: i18n.t('插件'),
        icon: TOOLBAR_PLUGIN,
        showSelectedValue: false,
        dropdown: {
            icon: TOOLBAR_DROPDOWN,
            type: DropdownType.ADDON,
            checkSelectedValue: false,
            data: [{}],
        },
    },
].filter(Boolean);

export const generateImageItems = {
    id: ActionType.AI_PICTURE,
    tipMsg: i18n.t('对图片不满意？试试重新智能配图'),
    titleContent: i18n.t('智能配图'),
    icon: TOOLBAR_GENERATE_IMAGE,
    dropdown: {
        icon: TOOLBAR_DROPDOWN,
        type: DropdownType.CUSTOM,
        checkSelectedValue: false,
        data: [
            {
                id: ActionType.REPLACE_PICTURE_GENERATE,
                content: i18n.t('重新生成'),
            },
            {
                id: ActionType.REPLACE_PICTURE_SEARCH,
                content: i18n.t('联网搜图'),
            },
        ],
    },
};

export const generateImageLiteItems = {
    id: ActionType.AI_PICTURE,
    icon: TOOLBAR_GENERATE_IMAGE,
    dropdown: {
        icon: TOOLBAR_DROPDOWN,
        type: DropdownType.CUSTOM,
        checkSelectedValue: false,
        data: [
            {
                id: ActionType.REPLACE_PICTURE_SEARCH,
                content: i18n.t('联网搜图'),
            },
        ],
    },
};

export const ImageReplaceWithAI = {
    id: ActionType.REPLACE_PICTURE,
    tipMsg: i18n.t('更换图片'),
    icon: TOOLBAR_PICTURE,
    showSelectedValue: false,
    dropdown: {
        icon: TOOLBAR_DROPDOWN,
        type: DropdownType.CUSTOM,
        checkSelectedValue: false,
        data: [
            {
                id: ActionType.REPLACE_PICTURE_FROM_FILE,
                content: i18n.t('选择图片'),
            },
            {
                id: ActionType.REPLACE_PICTURE_FROM_CLIPBOARD,
                content: i18n.t('来自剪贴板'),
            },
            {
                id: ActionType.REPLACE_PICTURE_SEARCH,
                content: i18n.t('联网搜图'),
            },
        ],
    },
};

export const ImageReplaceWithoutAI = {
    id: ActionType.REPLACE_PICTURE,
    tipMsg: i18n.t('更换图片'),
    icon: TOOLBAR_PICTURE,
    showSelectedValue: false,
    dropdown: {
        icon: TOOLBAR_DROPDOWN,
        type: DropdownType.CUSTOM,
        checkSelectedValue: false,
        data: [
            {
                id: ActionType.REPLACE_PICTURE_FROM_FILE,
                content: i18n.t('选择图片'),
            },
            {
                id: ActionType.REPLACE_PICTURE_FROM_CLIPBOARD,
                content: i18n.t('来自剪贴板'),
            },
        ],
    },
};

export const ImageFillQuickItemsForImage = [
    dataWithId(ActionType.TAILOR_PIC),
    ImageReplaceWithoutAI,
    {
        id: ActionType.RESET_PICTURE,
        tipMsg: i18n.t('重置图片'),
        icon: TOOLBAR_PIC_RESTORE,
        showSelectedValue: false,
    },
    { id: ActionType.DIVIDER },
];

/**
 * 图片填充
 * @type {*[]}
 */
export const ImageFillQuickItems = ImageFillQuickItemsForImage.concat([
    dataWithId(ActionType.FILLCOLOR),
    dataWithId(ActionType.BORDERCOLOR),
    dataWithId(ActionType.EFFECT),
]).concat(CommonQuickItems);

/**
 * 图片快捷设置
 * @type {*[]}
 */
export const ImageQuickItems = [
    dataWithId(ActionType.TAILOR_PIC),
    ImageReplaceWithoutAI,
    {
        id: ActionType.RESET_PICTURE,
        tipMsg: i18n.t('重置图片'),
        icon: TOOLBAR_PIC_RESTORE,
        showSelectedValue: false,
        dropdown: {
            icon: TOOLBAR_DROPDOWN,
            type: DropdownType.CUSTOM,
            checkSelectedValue: false,
            data: [
                {
                    id: ActionType.RESET_PICTURE_SELF,
                    content: i18n.t('重置图片'),
                },
                {
                    id: ActionType.RESET_PICTURE_SIZE,
                    content: i18n.t('重置图片及其大小'),
                },
            ],
        },
    },
    ENABLE_OCR && {
        id: ActionType.OCR_PICTURE,
        tipMsg: i18n.t('提取图片文字'),
        icon: TOOLBAR_IMAGE_RECOGNITION,
        showSelectedValue: false,
    },
    dataWithId(ActionType.BORDERCOLOR),
    dataWithId(ActionType.EFFECT),
]
    .concat(CommonQuickItems)
    .concat(addonItem)
    .filter(Boolean);


/**
 * 图表快捷设置
 * @type {*[]}
 */
export const ChartQuickItems = ([
    {
        id: ActionType.EDIT_CHART_COMPONENT,
        tipMsg: i18n.t('图表元素'),
        icon: INSERT_CHART,
        showSelectedValue: false,
        dropdown: {
            icon: TOOLBAR_DROPDOWN,
            type: DropdownType.CHART_COMPONENT,
            data: [{}],
        },
    },
] as ToolListType[])
    .concat(CommonQuickItems)
    .concat(MoreSettingItems);

/**
 * 附件快捷设置
 * @type {*[]}
 */
export const attachmentCommonItems = [
    ENABLE_DOWNLOAD_MEDIA && {
        id: ActionType.DOWNLOAD_ATTACHMENT,
        content: i18n.t('下载附件'),
        showSelectedValue: false,
    },
    ENABLE_DOWNLOAD_MEDIA && dataWithId(ActionType.DIVIDER),
    {
        id: ActionType.DELETE_ATTACHMENT,
        tipMsg: i18n.t('删除'),
        icon: TOOLBAR_ATTACHMENT_DELETE,
        showSelectedValue: false,
    },
].filter(Boolean);

/**
 * 编辑Latex公式快捷设置
 * @param {Array} keys 要显示按钮的关键词集合，例如: ['editAddon']，表示显示“编辑公式按钮”
 * @return {Array} 配置选项
 */
export const LatexQuickItems = (keys: string[] = []) => {
    const table = {
        editAddon: [
            {
                id: ActionType.REPLACE_WITH_LATEX,
                content: i18n.t('编辑公式'),
                style: { margin: '0 3px', padding: '0 4px' },
            },
            {
                id: ActionType.DIVIDER,
            },
        ],
        latexOCR: [
            {
                id: ActionType.OCR_WITH_LATEX,
                content: i18n.t('识别公式'),
            },
            {
                id: ActionType.DIVIDER,
            },
        ],
    };
    const latexTable = ENABLE_LATEX ? keys.map(key => table[key]).flat() : [];
    return [
        ...latexTable,
        dataWithId(ActionType.FILLCOLOR),
        dataWithId(ActionType.BORDERCOLOR),
        dataWithId(ActionType.EFFECT),
        ...CommonQuickItems,
    ].filter(Boolean);
};

/**
 * 表格快捷设置
 * @type {*[]}
 */
export const TableQuickItems = [
    dataWithId(ActionType.FILLCOLOR),
    dataWithId(ActionType.BORDERCOLOR),
    // dataWithId(ActionType.EFFECT),
    dataWithId(ActionType.BORDERSTYLE),
    {
        id: ActionType.MERGE_CELL,
        value: false,
        tipMsg: i18n.t('合并单元格'),
        icon: TOOLBAR_MERGECELL,
    },
].concat(CommonQuickItems);

/**
 * 组合快捷设置
 * @type {*[]}
 */
export const GroupQuickItems = [
    dataWithId(ActionType.FILLCOLOR),
    dataWithId(ActionType.BORDERCOLOR),
    dataWithId(ActionType.EFFECT),
    {
        id: ActionType.REMOVEGROUP,
        tipMsg: i18n.t('取消组合'),
        icon: TOOLBAR_REMOVEGROUP,
    },
].concat(CommonQuickItems);

export const GroupQuickItemWithMedia = ([
    {
        id: ActionType.REMOVEGROUP,
        tipMsg: i18n.t('取消组合'),
        icon: TOOLBAR_REMOVEGROUP,
    },
] as ToolListType[]).concat(CommonQuickItems as ToolListType[]);

/**
 * SmartArt 快捷设置
 * @type {*[]}
 */
export const SmartArtQuickItems = ([
    {
        id: ActionType.CONVERT_TO_GROUP,
        tipMsg: i18n.t('转成组合'),
        icon: TOOLBAR_ADDGROUP, // to do，需要更换图标
    },
] as ToolListType[]).concat(CommonQuickItems as ToolListType[]);

// 多选包括多个图片或者多个图片填充的形状时，浮动工具栏的裁剪按钮仅一个热区
const multiImageTailor = {
    id: ActionType.MULTI_TAILOR_PIC,
    tipMsg: i18n.t('裁剪'),
    title: i18n.t('裁剪'),
    icon: TOOLBAR_CLIP,
    dropdown: {
        icon: TOOLBAR_DROPDOWN,
        type: DropdownType.CROP,
        data: [{}],
    },
};

export const MultiImageFillQuickItemsForImage = [
    multiImageTailor,
    ImageReplaceWithoutAI,
    {
        id: ActionType.RESET_PICTURE,
        tipMsg: i18n.t('重置图片'),
        icon: TOOLBAR_PIC_RESTORE,
        showSelectedValue: false,
    },
    { id: ActionType.DIVIDER },
];

/**
 * 多选或者组合包括图片或者图片填充时
 */
export const MultiWithImageItems: ToolListType[] = [
    multiImageTailor,
    {
        id: ActionType.RESET_PICTURE,
        tipMsg: i18n.t('重置图片'),
        icon: TOOLBAR_PIC_RESTORE,
        showSelectedValue: false,
    },
    { id: ActionType.DIVIDER },
];

//  多选包括图片或者图片填充时的快速工具栏
export const FloatToolbarWithImageItems = MultiWithImageItems.concat(FloatToolbarsItems);
//  多选包括至少一个图片或者图片填充的形状时的快速工具栏
export const FloatToolbarWithAtLeastOneImageItems = MultiImageFillQuickItemsForImage.concat(FloatToolbarsItems);

// 组合包括图片
export const GroupQuickItemsWithImage = MultiWithImageItems.concat(GroupQuickItems as ToolListType[]);
// 组合包括图片和音视频
export const GroupQuickItemWithMediaAndImage = MultiWithImageItems.concat(GroupQuickItemWithMedia);
